// @flow
import * as React from 'react';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import type { InjectIntlProvidedProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import Label from 'box-ui-elements/es/components/label/Label';
import { Radio, Checkbox, InlineError } from '@box/blueprint-web';
import css from './list-field-submitter.module.scss';
import { TYPE_RADIO_FIELD } from '../../../../../constants';

type Props = {
  className?: string,
  description?: React.Node,
  error?: React.Node | string,
  fieldType: string,
  isDisabled: boolean,
  isInTwoColumn?: boolean,
  isRequired?: boolean,
  label: React.Node,
  onChange: Function,
  options: string[],
  selectedValues: string[],
} & InjectIntlProvidedProps;

type ListItemsProps = {
  isDisabled: boolean,
  onChange: Function,
  options: string[],
};

const RadioGroup = (props: ListItemsProps) => {
  const { onChange, options, isDisabled } = props;

  const handleChange = value => {
    onChange([value]);
  };

  return (
    <Radio.Group
      data-testid="radio-group"
      onValueChange={handleChange}
      orientation="vertical"
    >
      {options.map(option => (
        <Radio.Item
          key={uniqueId('radio-item')}
          disabled={isDisabled}
          label={option}
          value={option}
        />
      ))}
    </Radio.Group>
  );
};

const CheckboxGroup = (
  props: ListItemsProps & { selectedValues: string[] },
) => {
  const { onChange, options, selectedValues, isDisabled } = props;

  const handleCheckChange = (checked: boolean, value: string) => {
    const previouslySelected = selectedValues;
    let changedSelected;
    if (checked === false) {
      changedSelected = previouslySelected.filter(item => {
        return item !== value;
      });
    } else {
      // only add item if currently not in selected values
      if (previouslySelected.indexOf(value) === -1) {
        previouslySelected.push(value);
      }
      changedSelected = previouslySelected;
    }

    onChange(changedSelected);
  };

  return (
    <div>
      {options.map(option => (
        <Checkbox.Item
          key={`${option}-${uniqueId()}`}
          checked={selectedValues.includes(option)}
          className={css.checkboxItem}
          disabled={isDisabled}
          label={option}
          onCheckedChange={checked => handleCheckChange(checked, option)}
          value={option}
        />
      ))}
    </div>
  );
};

const ListFieldSubmitter = React.memo<Props>((props: Props) => {
  const {
    className,
    isInTwoColumn,
    label,
    description,
    error,
    fieldType,
    onChange,
    selectedValues,
    isRequired,
    options,
    isDisabled,
  } = props;

  const isRadio = TYPE_RADIO_FIELD === fieldType;

  const hasError = !!error;
  const classes = classNames(className, 'list-field-container', {
    [css.halfColumnWidth]: isInTwoColumn,
  });

  const errorMessageID = React.useRef(uniqueId('errorMessage')).current;
  const descriptionID = React.useRef(uniqueId('description')).current;

  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-required': isRequired,
    'aria-describedby': description ? descriptionID : undefined,
    'aria-errormessage': errorMessageID,
  };

  return (
    <div className={classes}>
      <Label text={label} />
      {!!description && (
        <div className="list-field-description" id={descriptionID}>
          {description}
        </div>
      )}
      <div className={css.listGroup}>
        {isRadio ? (
          <RadioGroup
            {...ariaAttrs}
            isDisabled={isDisabled}
            onChange={onChange}
            options={options}
          />
        ) : (
          <CheckboxGroup
            {...ariaAttrs}
            isDisabled={isDisabled}
            onChange={onChange}
            options={options}
            selectedValues={selectedValues}
          />
        )}
      </div>
      {isRequired && !!error && (
        <InlineError className={classNames({ [css.error]: isRadio })}>
          {error}
        </InlineError>
      )}
    </div>
  );
});

export { ListFieldSubmitter };
export default injectIntl(ListFieldSubmitter);
